import { Fade, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import MediaField from 'Components/Forms/MediaField';
import TextInputField from 'Components/Forms/TextInputField';
import useWelcomeSlides from 'hooks/useWelcomeSlides';
import { companySchema } from 'FeatureModules/Setting/schemas/company.schema';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import { FastField as Field, Form, Formik } from 'formik';
import React, { FC, ReactNode } from 'react';
import { ECustomerPlan, ICompany } from 'FeatureModules/Auth/types/company.types';
import { ImageVariant } from 'FeatureModules/Media/types/media.types';
import { Headline, ImageContainer, PageContainer, SkipButton } from './styles';
import CountryMultiselectField from 'Components/Forms/CountryMultiselectField';

export interface ICompanyFormValues {
  name: string;
  onlineShop?: string;
  website: string;
  logo?: ImageVariant;
}

interface ICompanyFormProps {
  company: ICompany;
  active: boolean;
  children: (props: { submitForm: () => void; isSubmitting: boolean }) => ReactNode;
  onSubmit: (values: ICompanyFormValues) => void;
}

const StyledForm = styled(Form)(() => ({
  height: '100%',
}));

const LabelLogo = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const FormContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

const CompanyGrid = styled(Grid)(() => ({
  height: '80%',
  padding: 0,
}));

export const LeftScrollContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  flexDirection: 'column',
  padding: theme.spacing(8, 5, 8, 5),
  width: '45%',
  overflowY: 'auto',
}));

const CompanyForm: FC<ICompanyFormProps> = ({ company, onSubmit, children, active }) => {
  const {
    name,
    onlineShop,
    website,
    logo,
    description,
    eoriNumber,
    vatNumber,
    countriesOfBusiness,
  } = company;
  const { nextSlide } = useWelcomeSlides();
  const { plan } = useCompanyPlanContext();
  const isPartner = plan === ECustomerPlan.FREEMIUM;

  return (
    <Formik
      initialValues={{
        name,
        onlineShop,
        website,
        logo,
        description,
        eoriNumber,
        vatNumber,
        countriesOfBusiness,
      }}
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={companySchema}
      onSubmit={values =>
        onSubmit({
          ...values,
        })
      }
    >
      {({ isSubmitting, submitForm }) => (
        <StyledForm>
          <FormContainer>
            <PageContainer>
              <LeftScrollContainer>
                <CompanyGrid container spacing={2}>
                  <Grid item xs={12}>
                    <Headline variant="TITLE_LARGE">
                      Let’s get your <br /> account set up
                    </Headline>
                  </Grid>
                  <Grid item xs={12}>
                    <TextInputField label="Company name" name="name" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInputField
                      label="EORI number"
                      name="eoriNumber"
                      placeholder='e.g. "DE123456789012345678"'
                      helperText="If you import or export goods into or out of the European Union (EU), you will need to add your company EORI number. This number is valid throughout the EU. It is used as a common reference number for interactions with the customs authorities in any EU member State."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInputField
                      label="VAT number"
                      name="vatNumber"
                      placeholder='e.g. "DE123456789"'
                      helperText="If you import or export goods into or out of the European Union (EU), you will need to add your company tax identification number (VAT). In the EU, a VAT identification number can be verified online at the EU's official VIES website. It confirms that the number is currently allocated and can provide the name or other identifying details of the entity to whom the identifier has been allocated."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInputField
                      multiline
                      maxlength={300}
                      maxRows={5}
                      minRows={3}
                      placeholder='e.g. "We are a company that specializes in..."'
                      label="Description"
                      name="description"
                      helperText="Briefly describe what your company does, including key products, services, areas of expertise and daily activities. Keep it short and clear—this will help others quickly understand your business."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInputField
                      label="Company Website"
                      name="website"
                      placeholder="https://website.com"
                    />
                  </Grid>

                  {!isPartner && (
                    <Grid item xs={12}>
                      <TextInputField
                        label="Online Shop"
                        name="onlineShop"
                        placeholder="https://onlineshop.com"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <CountryMultiselectField
                      name="countriesOfBusiness"
                      placeholder="Select countries"
                      showCountryRisk
                      size="medium"
                      label="Countries of business"
                      helperText="Select the countries where your company operates. This will allow filtering based on country."
                      data-testid="countries-of-business"
                    />
                  </Grid>
                  <Grid item xs={12} height={64} />
                </CompanyGrid>
              </LeftScrollContainer>
              <Fade in={active} timeout={500}>
                <ImageContainer>
                  <SkipButton onClick={nextSlide}>Add this later</SkipButton>
                  <LabelLogo variant="h6">Company logo</LabelLogo>
                  <Field
                    cropDisabled
                    component={MediaField}
                    fullWidth
                    singleUpload
                    name="logo"
                    legacyImage={false}
                    styles={{
                      borderRadius: 10,
                      width: 200,
                      height: 200,
                    }}
                  />
                </ImageContainer>
              </Fade>
            </PageContainer>
            {children({ submitForm, isSubmitting })}
          </FormContainer>
        </StyledForm>
      )}
    </Formik>
  );
};

export default CompanyForm;
